import React, { Suspense, useRef, useState, useEffect, useMemo } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import AnimatedModel from '../models/AnimatedModel';
import FloorPlatformModel from '../models/FloorPlatformModel';
import EnvironmentOne from './SkyEnv';
import * as THREE from 'three';
import useTheme from '../utils/useTheme';

const Loader = () => (
  <div
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 18,
      color: '#a5b4fc',
      textAlign: 'center',
      fontFamily: '"DM Sans", "Heebo", "Segoe UI", system-ui, sans-serif',
    }}
  >
    <style>{`
      @keyframes loaderSpin { to { transform: rotate(360deg); } }
      @keyframes loaderPulse { 0%,100% { opacity: 0.35; } 50% { opacity: 1; } }
    `}</style>
    <div
      aria-hidden="true"
      style={{
        width: 52,
        height: 52,
        borderRadius: '50%',
        border: '3px solid rgba(99,102,241,0.18)',
        borderTopColor: '#6366f1',
        borderRightColor: '#06b6d4',
        animation: 'loaderSpin 0.9s linear infinite',
        boxShadow: '0 0 24px rgba(99,102,241,0.25)',
      }}
    />
    <div style={{ fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.02em', animation: 'loaderPulse 1.6s ease-in-out infinite' }}>
      טוען עולם תלת־ממדי...
    </div>
  </div>
);

// Particle System
const PARTICLE_COUNT = 1000;

const PORTAL_TICKS = Array.from({ length: 12 }, (_, i) => (i / 12) * Math.PI * 2);
const PORTAL_NODES = [0.12, 2.15, 4.28];

/**
 * A restrained holographic frame behind the hero character.
 * Broken arcs and small editor-like ticks add depth and a game-development
 * identity without becoming another focal object in front of the model.
 */
const HeroPortal = ({ isMobile, reduced }) => {
  const theme = useTheme();
  const root = useRef();
  const innerArc = useRef();
  const outerArc = useRef();
  const nodes = useRef([]);
  const light = theme === 'light';

  const primary = light ? '#2F79AD' : '#5CA7DD';
  const secondary = light ? '#278D89' : '#34D9B5';
  const blending = light ? THREE.NormalBlending : THREE.AdditiveBlending;

  useFrame(({ clock }, delta) => {
    if (reduced) return;
    if (root.current) root.current.rotation.z += delta * 0.035;
    if (innerArc.current) innerArc.current.rotation.z -= delta * 0.13;
    if (outerArc.current) outerArc.current.rotation.z += delta * 0.075;

    nodes.current.forEach((node, i) => {
      if (!node) return;
      const pulse = 0.86 + Math.sin(clock.elapsedTime * 1.8 + i * 2.1) * 0.16;
      node.scale.setScalar(pulse);
      node.rotation.x += delta * (0.35 + i * 0.08);
      node.rotation.y += delta * (0.45 + i * 0.1);
    });
  });

  const material = (color, opacity) => (
    <meshBasicMaterial
      color={color}
      transparent
      opacity={opacity}
      blending={blending}
      depthWrite={false}
      toneMapped={false}
    />
  );

  return (
    <group
      ref={root}
      position={isMobile ? [0, 3.45, -1.45] : [-1.35, 2.45, -1.35]}
      scale={isMobile ? 0.78 : 1}
    >
      {/* Soft centre wash: visible on both themes, never opaque enough to read
          as a disc behind the character. */}
      <mesh position={[0, 0, -0.03]}>
        <circleGeometry args={[1.52, 72]} />
        <shaderMaterial
          transparent
          depthWrite={false}
          blending={blending}
          uniforms={{
            uColor: { value: new THREE.Color(primary) },
            uOpacity: { value: light ? 0.075 : 0.13 },
          }}
          vertexShader={`
            varying vec2 vUv;
            void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `}
          fragmentShader={`
            uniform vec3 uColor;
            uniform float uOpacity;
            varying vec2 vUv;
            void main() {
              float d = distance(vUv, vec2(0.5)) * 2.0;
              float alpha = pow(1.0 - clamp(d, 0.0, 1.0), 1.7) * uOpacity;
              if (alpha < 0.003) discard;
              gl_FragColor = vec4(uColor, alpha);
            }
          `}
        />
      </mesh>

      <mesh ref={outerArc} rotation={[0, 0, -0.38]}>
        <torusGeometry args={[1.67, 0.014, 8, 120, Math.PI * 1.48]} />
        {material(primary, light ? 0.42 : 0.56)}
      </mesh>
      <mesh ref={innerArc} rotation={[0, 0, Math.PI * 0.72]}>
        <torusGeometry args={[1.34, 0.01, 8, 100, Math.PI * 1.18]} />
        {material(secondary, light ? 0.34 : 0.48)}
      </mesh>

      {/* Scene-editor ticks give the ring a technical HUD character. */}
      {PORTAL_TICKS.map((angle, i) => (
        <mesh
          key={angle}
          position={[Math.cos(angle) * 1.66, Math.sin(angle) * 1.66, 0]}
          rotation={[0, 0, angle]}
          scale={i % 3 === 0 ? [1.45, 1, 1] : [0.85, 1, 1]}
        >
          <boxGeometry args={[0.11, 0.018, 0.018]} />
          {material(i % 3 === 0 ? secondary : primary, light ? 0.45 : 0.62)}
        </mesh>
      ))}

      {/* Three data nodes orbit with the frame and pulse independently. */}
      {PORTAL_NODES.map((angle, i) => (
        <mesh
          key={angle}
          ref={(node) => { nodes.current[i] = node; }}
          position={[Math.cos(angle) * 1.49, Math.sin(angle) * 1.49, 0.04]}
        >
          <octahedronGeometry args={[i === 1 ? 0.065 : 0.05, 0]} />
          {material(i === 1 ? secondary : primary, light ? 0.7 : 0.9)}
        </mesh>
      ))}

      <pointLight color={secondary} intensity={light ? 0.22 : 0.45} distance={4} decay={2} />
    </group>
  );
};

const Particles = () => {
  const points = useRef();
  const particleTexture = useLoader(THREE.TextureLoader, '/textures/circle_05.png');
  const theme = useTheme();
  // Additive light is what makes these glow on navy, but on a white page it is
  // a no-op — white plus anything is still white, so they vanish. On the light
  // theme they draw normally in a colour that can actually sit on white.
  const light = theme === 'light';

  // Built once. It used to be regenerated on every render, which reallocated
  // 3000 floats and re-scattered every particle.
  const particlesPosition = useMemo(() => {
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
    }
    return positions;
  }, []);

  const uniforms = useMemo(
    () => ({
      uTexture: { value: particleTexture },
      uColor: { value: new THREE.Color(light ? '#4A72B8' : '#6ea5ff') },
      uIntensity: { value: light ? 1 : 1.8 },
    }),
    [particleTexture, light]
  );

  useFrame(({ clock }) => {
    const elapsedTime = clock.getElapsedTime();
    if (points.current) {
      points.current.rotation.y = elapsedTime * 0.1;
    }
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={particlesPosition}
          count={PARTICLE_COUNT}
          itemSize={3}
        />
      </bufferGeometry>
      <shaderMaterial
        attach="material"
        uniforms={uniforms}
        vertexShader={`
          void main() {
            vec4 modelPosition = modelMatrix * vec4(position, 1.0);
            vec4 viewPosition = viewMatrix * modelPosition;
            gl_Position = projectionMatrix * viewPosition;
            gl_PointSize = 40.0 / -viewPosition.z;
          }
        `}
        fragmentShader={`
          uniform sampler2D uTexture;
          uniform vec3 uColor;
          uniform float uIntensity;

          void main() {
            // Read only the alpha as a mask. The sprite's RGB is premultiplied
            // (it fades toward black as it fades out), so tinting with it —
            // and then forcing alpha to 1 as the old x10 did — turned the soft
            // falloff into an opaque disc with a dark rim.
            float mask = texture2D(uTexture, gl_PointCoord).a;
            if (mask < 0.01) discard;

            // Additive: colour is constant and the mask rides in alpha, so the
            // blend contributes uColor * mask. Transparent texels add nothing,
            // which is why no corner can darken the scene behind it.
            gl_FragColor = vec4(uColor * uIntensity, mask);
          }
        `}
        transparent={true}
        blending={light ? THREE.NormalBlending : THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
};

const ZoomAnimation = () => {
  const cameraStart = useRef(null);

  useFrame(({ camera, clock }) => {
    if (!cameraStart.current) {
      cameraStart.current = camera.position.clone();
    }
    const elapsedTime = clock.getElapsedTime();
    const t = Math.min(elapsedTime / 2.5, 1);
    const eased = 1 - Math.pow(1 - t, 3);
    camera.position.lerpVectors(cameraStart.current, new THREE.Vector3(-0.25, 2.45, 5.91), eased);
    camera.lookAt(0, 2, 0);
  });

  return null;
};

const ThreeDCanvas = ({ onColorChange }) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [reduced, setReduced] = useState(false);
  const theme = useTheme();
  const mousePosition = useRef({ x: 0, y: 0 });
  const [camera, setCamera] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const read = () =>
      setReduced(mq.matches || document.documentElement.classList.contains('stop-animations'));
    read();
    mq.addEventListener('change', read);
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => {
      mq.removeEventListener('change', read);
      observer.disconnect();
    };
  }, []);

  const updatePointerPosition = (clientX, clientY) => {
    const x = (clientX / window.innerWidth) * 2 - 1;
    const y = -(clientY / window.innerHeight) * 2 + 1;
    mousePosition.current = { x, y };
  };

  const handleMouseMove = (event) => {
    updatePointerPosition(event.clientX, event.clientY);
  };

  const handleTouchMove = (event) => {
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      updatePointerPosition(touch.clientX, touch.clientY);
    }
  };

  return (
    // Decorative: everything the scene says is also in the copy beside it, and
    // the click-the-character easter egg is delight rather than function. Left
    // exposed, the bare <canvas> reaches a screen reader as an unnamed element.
    <div
      className="webgl-interaction-surface"
      style={{ position: 'relative', width: '100%', height: '100%', touchAction: isMobile ? 'pan-y' : 'none' }}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      aria-hidden="true"
    >
      <Suspense fallback={<Loader />}>
        <Canvas
          shadows
          camera={{ position: [-2.0, 2.2, 11.0], fov: isMobile ? 58 : 48 }}
          onCreated={({ camera }) => setCamera(camera)}
          style={{ touchAction: isMobile ? 'pan-y' : 'none' }}
          // r3f re-measures the container on every scroll by default, and any
          // sub-pixel difference rebuilds the drawing buffer. The hero never
          // changes size while scrolling, so that work is pure cost.
          resize={{ scroll: false, debounce: { scroll: 50, resize: 0 } }}
          // Keep enough density for thin model edges on retina phones. The old
          // 1.5 cap was visibly below the 2-3x panels most phones use and made
          // the whole scene look soft before post-processing even began.
          dpr={[1, 2]}
        >
          <Environment files="/environment.hdr" background={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 10, 5]} intensity={1.5} castShadow />

          {/* Animated Model with Offset adjusted for the palm */}
          <AnimatedModel
            position={isMobile ? [0.0, 1.7, 0] : [-1.5, 0.5, 0]}
            mousePosition={mousePosition}
            camera={camera}
            onColorChange={onColorChange}
            isMobile={isMobile}
            lightOffset={[0, 0.3, 0.05]}
          />

          {!isMobile && <FloorPlatformModel />}

          <HeroPortal isMobile={isMobile} reduced={reduced} />

          {/* Environment */}
          <EnvironmentOne />

          {/* Particle System */}
          <Particles />

          {/* Zoom Animation */}
          <ZoomAnimation />

          {/* Post Processing: Godot-style Bloom */}
          <EffectComposer disableNormalPass>
            <Bloom
              // A white page is itself brighter than 0.5, so on the light
              // theme the background would bloom over everything.
              luminanceThreshold={theme === 'light' ? 0.92 : 0.5}
              mipmapBlur={true}
              intensity={0.03}
            />
          </EffectComposer>

          {/* Orbit Controls */}
          {/* OrbitControls writes `touch-action: none` to its DOM element even
              while disabled. Do not mount it on phones, so a drag beginning
              anywhere over the WebGL scene remains native page scrolling. */}
          {!isMobile && (
            <OrbitControls
              target={[-0.25, 2.45, 4.5]}
              enableZoom={false}
              enablePan={false}
              maxPolarAngle={Math.PI / 2}
              minPolarAngle={Math.PI / 4}
            />
          )}
        </Canvas>
      </Suspense>
    </div>
  );
};

export default ThreeDCanvas;
